import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ProviderService } from '../../provider.service';
import { EasyRxApiService } from '../../easyrx-api.service';
import { MedicationPrescription } from '../../api-client.service';
import {
  buildEasyRxTimelineSteps,
  EASYRX_ORDER_SUFFIX,
  easyRxPillColor,
  easyRxStatusLabel,
  toEasyRxStatusRecord,
} from '../../models/easyrx.model';
import {
  PharmacyEscriptDescriptor,
  EscriptStatusRecord,
  EscriptSendContext,
  EscriptSendResult,
  EscriptTimelineStep,
  UnifiedStatusView,
} from '../pharmacy-escript.descriptor';
import { UnifiedPharmacy, easyRxToUnified } from '../unified-pharmacy.model';
import { OnDemandStatusSource } from '../sources/on-demand-status.source';

/**
 * EasyRx vendor descriptor — doctor-triggered fetch status (no webhook), client-generated order
 * number, financial summary (no per-line breakdown), no cancel (phase 2). Reuses the generic send
 * dialog, status modal and `OnDemandStatusSource` unchanged.
 */
@Injectable({ providedIn: 'root' })
export class EasyRxDescriptor implements PharmacyEscriptDescriptor {
  readonly id = 'easyrx';
  readonly displayName = 'EasyRx';
  readonly chip = { kind: 'text' as const, value: 'ER', color: '#0e8f9e' };
  readonly sentField = 'SentToEasyRx';
  readonly identifierField = 'EasyRxOrderNumber';
  readonly orderNumberSuffix = EASYRX_ORDER_SUFFIX;
  readonly transferAsOriginalScript = false;
  readonly statusMode = 'fetch' as const;
  readonly capabilities = { canCancel: false, showFinancials: true, showLineItems: false };
  // Catalogue comes from EasyRx's live API (via Florence), not a CSV upload — the admin screen
  // shows a "Refresh from API" action that rewrites the unified store.
  readonly ingest = {
    kind: 'api' as const,
    // practiceNo is the record identity and storeName the display name — a record missing either is unusable
    fetchAll: (practiceId: string) =>
      this.easyRxApi
        .getAllPharmacies(practiceId)
        .then(pharmacies => pharmacies.filter(p => !!p.practiceNo && !!p.storeName).map(easyRxToUnified)),
  };

  constructor(
    private providerService: ProviderService,
    private easyRxApi: EasyRxApiService,
    private onDemand: OnDemandStatusSource
  ) {}

  allowed$(): Observable<boolean> {
    return this.providerService.IsAllowedEasyRxScripts$;
  }

  readScriptId(prescription: MedicationPrescription): string | null {
    return (
      (prescription.SentToEasyRx && prescription.SentToEasyRx.orderNumber) ||
      prescription.EasyRxOrderNumber ||
      this.easyRxApi.getOrderNumber(prescription)
    );
  }

  async send(ctx: EscriptSendContext): Promise<EscriptSendResult> {
    const orderNumber = await this.easyRxApi.sendPrescriptionToEasyRx(
      ctx.tenantId,
      ctx.prescription,
      ctx.pharmacy.raw,
      ctx.pdfUrl,
      ctx.encounterId
    );
    return { identifier: orderNumber };
  }

  buildSentPayload(result: EscriptSendResult, pharmacy: UnifiedPharmacy): any {
    return { orderNumber: result.identifier, pharmacy: pharmacy.raw, Status: 'Sent' };
  }

  status$(practiceId: string, identifier: string, refresh$?: Observable<void>): Observable<EscriptStatusRecord | null> {
    return this.onDemand.fetch$(() => this.easyRxApi.getPrescriptionStatus(practiceId, identifier).then(toEasyRxStatusRecord), refresh$);
  }

  toStatusView(record: EscriptStatusRecord | null): UnifiedStatusView | null {
    if (!record) {
      return null;
    }
    const status = (record.status as string) || '';
    if (!status) {
      return { statusCode: record.statusCode ?? -1, statusLabel: 'Awaiting response from pharmacy...', pillColor: 'orange' };
    }
    return { statusCode: record.statusCode ?? -1, statusLabel: easyRxStatusLabel(status), pillColor: easyRxPillColor(status) };
  }

  buildTimelineSteps(record: EscriptStatusRecord): EscriptTimelineStep[] {
    return buildEasyRxTimelineSteps(record);
  }
}
